import AuthGuard from '@/components/feature/AuthGuard';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import HebrewDateBanner from '@/components/feature/HebrewDateBanner';
import StatsCardsRow from './components/StatsCardsRow';
import StreakWidget from './components/StreakWidget';
import ZmanimWidget from './components/ZmanimWidget';
import UpcomingRemindersWidget from './components/UpcomingRemindersWidget';
import WeeklyChartWidget from './components/WeeklyChartWidget';
import TodayStatusWidget from './components/TodayStatusWidget';
import MonthlyTrendWidget from './components/MonthlyTrendWidget';
import TestCallWidget from '@/components/feature/TestCallWidget';

function greetingWord(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function DashboardContent() {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const name = user?.user_metadata?.full_name || user?.email?.split('@')[0];

  return (
    <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
      {/* Per-page top bar */}
      <header className="hidden lg:flex h-16 items-center justify-between px-6 border-b border-background-300/40 bg-cream/60 dark:bg-midnight-deep/60 backdrop-blur flex-shrink-0">
        <h2 className="font-display text-xl font-bold text-foreground-950">Dashboard</h2>
        <div className="flex items-center gap-1">
          <button className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-500 hover:text-gold hover:bg-gold/10 transition cursor-pointer" title="Notifications">
            <i className="ri-notification-3-line text-lg" />
          </button>
          <button
            onClick={toggleTheme}
            className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-500 hover:text-gold hover:bg-gold/10 transition cursor-pointer"
            title="Toggle dark mode"
          >
            <i className={theme === 'dark' ? 'ri-sun-line text-lg' : 'ri-moon-line text-lg'} />
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 md:p-6">
        {/* Greeting banner */}
        <div className="relative overflow-hidden rounded-2xl border border-gold/20 bg-gradient-to-r from-surface-warm to-[#FEF3C7] dark:from-midnight-card dark:to-midnight px-6 py-5 mb-5">
          <div
            className="absolute top-0 right-0 w-40 h-40 opacity-[0.06] pointer-events-none"
            style={{
              backgroundImage:
                'repeating-linear-gradient(60deg, #D4A843 0 1px, transparent 1px 14px), repeating-linear-gradient(-60deg, #D4A843 0 1px, transparent 1px 14px)',
            }}
          />
          <h1 className="relative z-10 font-display text-2xl md:text-[28px] font-bold text-midnight dark:text-white">
            {greetingWord()}, {name}
          </h1>
        </div>

        {/* Hebrew Date + Holiday Banner */}
        <div className="mb-5">
          <HebrewDateBanner />
        </div>

        {/* Stats cards */}
        <div className="mb-5">
          <StatsCardsRow />
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Left column — wider */}
          <div className="lg:col-span-2 space-y-5">
            <TodayStatusWidget />
            <WeeklyChartWidget />
            <MonthlyTrendWidget />
          </div>

          {/* Right column — narrower */}
          <div className="space-y-5">
            <StreakWidget />
            <ZmanimWidget />
            <TestCallWidget />
            <UpcomingRemindersWidget />
          </div>
        </div>
      </main>
    </div>
  );
}

export default function DashboardPage() {
  return (
    <AuthGuard>
      <AppLayout>
        <DashboardContent />
      </AppLayout>
    </AuthGuard>
  );
}